import React, { ReactNode } from 'react';
import { Button } from '@/components/ui';

export interface PageToolbarProps {
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (val: string) => void;
  
  onFilterClick?: () => void;
  isFilterActive?: boolean;
  
  tabs?: { label: string; value: string }[];
  activeTab?: string;
  onTabChange?: (val: string) => void;
  
  primaryAction?: ReactNode;
  
  /** Cho phép tuỳ biến thêm các component phụ trợ bên cạnh Filter */
  extraLeft?: ReactNode;
  
  /** Cho phép tuỳ biến hoàn toàn nội dung Left thay vì dùng extraLeft */
  children?: ReactNode;
}

/**
 * PageToolbar - Thanh công cụ chuẩn (Search, Filter, Tabs, Create) theo 03-SkillToolbar.md
 */
export function PageToolbar({
  searchPlaceholder = 'Tìm kiếm...',
  searchValue,
  onSearchChange,
  onFilterClick,
  isFilterActive,
  tabs,
  activeTab,
  onTabChange,
  primaryAction,
  extraLeft,
  children
}: PageToolbarProps) {
  return (
    <div className="bg-white border-y border-slate-100 sticky top-[60px] z-20 mb-8 -mx-4 md:-mx-6 lg:-mx-7 px-4 md:px-6 lg:px-7 py-4 lg:py-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 w-full">
        
        {/* Left: Search, Filter & Tabs */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 flex-1">
          <div className="flex items-center gap-3">
            {/* Search */}
            {onSearchChange && (
              <div className="relative flex-1 max-w-md group">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-brand-600 transition-colors" />
                <input 
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchValue || ''}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="w-full h-12 lg:h-14 pl-12 pr-4 bg-slate-50 border border-slate-100 rounded-md focus:ring-2 focus:ring-blue-500/10 sm:text-sm text-xs text-slate-700 outline-none"
                />
              </div>
            )}

            {/* Filter */}
            {onFilterClick && (
              <Button 
                variant="outline"
                onClick={onFilterClick}
                className={`h-12 lg:h-14 px-5 rounded-md transition-all flex items-center gap-2 sm:text-sm text-xs ${
                  isFilterActive 
                    ? 'bg-blue-50 border-blue-200 text-blue-700' 
                    : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
              >
                <i className="ri-filter-3-line text-lg" />
                <span className="hidden lg:inline">Bộ lọc</span>
                {isFilterActive && <span className="w-2 h-2 rounded-full bg-blue-500" />}
              </Button>
            )}
            
            {extraLeft}
            {children}
          </div>

          {/* Tabs */}
          {tabs && tabs.length > 0 && (
            <div className="flex items-center gap-1 overflow-x-auto hide-scrollbar border-l-0 sm:border-l border-slate-200 sm:pl-4">
              {tabs.map(tab => (
                <Button 
                  key={tab.value}
                  variant="ghost"
                  onClick={() => onTabChange?.(tab.value)}
                  className={`px-4 py-2 rounded-md sm:text-sm text-xs whitespace-nowrap transition-colors ${
                    activeTab === tab.value 
                      ? 'bg-slate-900 text-white' 
                      : 'text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  {tab.label}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Right: Action */}
        {primaryAction && (
          <div className="flex shrink-0">
            {primaryAction}
          </div>
        )}
      </div>
    </div>
  );
}
